import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from './AuthContext';
import { playSound, SoundType } from '~/lib/audio';
import type { Rarity } from '../types';

export type InventoryCategory =
  | 'hat'
  | '1h-melee'
  | '2h-melee'
  | 'ranged'
  | 'magic'
  | 'leather-armor'
  | 'metal-armor';

export interface InventoryItemData {
  slug: string;
  name: string;
  description: string | null;
  category?: InventoryCategory;
  itemTypes: string[];
  slotsUsed: string[];
  isTwoHanded?: boolean;
  price: number;
  rarity: Rarity;
  spriteSheet: string;
  spriteFrameIndex: number;
  spriteTint?: string | null;
  statChanges?: Array<{
    stat: string;
    value: number;
  }>;
}

export interface InventoryEntry {
  itemSlug: string;
  item: InventoryItemData | null;
}

interface InventoryContextValue {
  inventory: InventoryEntry[];
  isLoading: boolean;
  error: string | null;
  refreshInventory: () => Promise<void>;
  addToInventory: (itemSlug: string) => Promise<boolean>;
  selectedItem: InventoryEntry | null;
  setSelectedItem: (item: InventoryEntry | null) => void;
}

const InventoryContext =
  createContext<InventoryContextValue | null>(null);

interface InventoryProviderProps {
  children: ReactNode;
}

export function InventoryProvider({
  children,
}: InventoryProviderProps) {
  const { isAuthenticated } = useAuth();
  const [inventory, setInventory] = useState<
    InventoryEntry[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItemState] =
    useState<InventoryEntry | null>(null);

  const setSelectedItem = useCallback(
    (item: InventoryEntry | null) => {
      if (item) {
        playSound(SoundType.PickItem);
      }
      setSelectedItemState(item);
    },
    []
  );

  const loadInventory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await apiGet<{
      inventory: InventoryEntry[];
    }>('/api/me/inventory');

    if (result.ok) {
      setInventory(result.data.inventory);
    } else {
      setError(result.error.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    loadInventory();
  }, [isAuthenticated, loadInventory]);

  const addToInventory = useCallback(
    async (itemSlug: string): Promise<boolean> => {
      const result = await apiPost<{
        inventoryItem: InventoryEntry;
      }>('/api/me/inventory', { itemSlug });

      if (result.ok) {
        setInventory(prev => {
          // Check if already owned (shouldn't happen, but be safe)
          const existing = prev.find(
            item => item.itemSlug === itemSlug
          );
          if (existing) return prev;
          return [result.data.inventoryItem, ...prev];
        });
        return true;
      }

      return false;
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      inventory,
      isLoading,
      error,
      refreshInventory: loadInventory,
      addToInventory,
      selectedItem,
      setSelectedItem,
    }),
    [
      inventory,
      isLoading,
      error,
      loadInventory,
      addToInventory,
      selectedItem,
    ]
  );

  return (
    <InventoryContext.Provider value={contextValue}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory(): InventoryContextValue {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error(
      'useInventory must be used within an InventoryProvider'
    );
  }
  return context;
}
